// Patient allergy vs prescribed medicine — pure logic, no React / path aliases (unit-testable).
//
// Patient allergies are free text ("Penicillin, sulfa"). The catalog has no reliable drug classes
// (molecules.drug_class is almost always NULL), so drug families are recognised here from
// ingredient names. Direct matches are errors; known cross-reactions are warnings.

export type AllergyHit = {
  level: 'error' | 'warning';
  allergy: string;       // the patient's allergy term, as written
  ingredient: string;    // the medicine ingredient that triggered it
  text: string;          // one-line message
};

type Family = {
  key: string;
  label: string;                 // "a penicillin"
  aliases: RegExp;               // how patients / staff write the allergy
  members: RegExp;               // ingredient names in this family
  crossReacts?: { family: string; note: string }[];
};

const FAMILIES: Family[] = [
  {
    key: 'penicillin', label: 'a penicillin',
    aliases: /\b(penicillins?|pcn|beta[- ]?lactams?|amoxy?cillin|amoxil|augmentin|ampicillin|cloxacillin|mox(ikind)?|novamox)\b/,
    members: /\b(\w*cillin|penicillin\w*|benzathine|mecillinam|pivmecillinam)\b/,
    crossReacts: [
      { family: 'cephalosporin', note: 'possible cross-reaction with penicillin allergy' },
      { family: 'carbapenem', note: 'possible cross-reaction with penicillin allergy' },
    ],
  },
  {
    key: 'cephalosporin', label: 'a cephalosporin',
    aliases: /\b(cephalosporins?|cef\w*|ceph\w*)\b/,
    members: /\b(cef\w+|ceph\w+|loracarbef)\b/,
    crossReacts: [{ family: 'penicillin', note: 'possible cross-reaction with cephalosporin allergy' }],
  },
  {
    key: 'carbapenem', label: 'a carbapenem',
    aliases: /\b(carbapenems?|\w+penem)\b/,
    members: /\b\w+penem\b/,
  },
  {
    // Sulfonamide antibiotics only — "zinc sulfate", "polysulfate" etc. are not sulfa drugs
    key: 'sulfa', label: 'a sulfonamide (sulfa) antibiotic',
    aliases: /\b(sulfa|sulpha|sulfonamides?|sulphonamides?|co-?trimoxazole|cotrimoxazole|septran|bactrim)\b/,
    members: /\b(sulfameth\w*|sulphameth\w*|sulfadiaz\w*|sulfadox\w*|sulfasalaz\w*|sulphasalaz\w*|sulfacetam\w*|sulfisox\w*|sulfamethopyrazine|co-?trimoxazole|cotrimoxazole)\b/,
  },
  {
    key: 'nsaid', label: 'an NSAID',
    aliases: /\b(nsaids?|painkillers? \(nsaid\)|ibuprofen|brufen|combiflam|diclofenac|voveran|naproxen|mefenamic|meftal|aceclofenac|ketorolac|nimesulide|aspirin|ecosprin|disprin)\b/,
    members: /\b(ibuprofen|diclofenac|naproxen|mefenamic|aceclofenac|ketorolac|indomethacin|indometacin|piroxicam|meloxicam|lornoxicam|etoricoxib|celecoxib|nimesulide|ketoprofen|dexketoprofen|flurbiprofen|etodolac|aspirin|acetylsalicylic)\b/,
  },
  {
    key: 'macrolide', label: 'a macrolide',
    aliases: /\b(macrolides?|azithromycin|azithral|azee|clarithromycin|erythromycin)\b/,
    members: /\b(azithromycin|clarithromycin|erythromycin|roxithromycin)\b/,
  },
  {
    key: 'quinolone', label: 'a fluoroquinolone',
    aliases: /\b((fluoro)?quinolones?|\w+floxacin|ciplox|norflox)\b/,
    members: /\b\w+floxacin\b/,
  },
  {
    key: 'tetracycline', label: 'a tetracycline',
    aliases: /\b(tetracyclines?|doxycycline|minocycline)\b/,
    members: /\b\w*cycline\b/,
  },
  {
    key: 'aminoglycoside', label: 'an aminoglycoside',
    aliases: /\b(aminoglycosides?|gentamicin|amikacin|tobramycin|streptomycin|neomycin)\b/,
    members: /\b(gentamicin|amikacin|tobramycin|streptomycin|neomycin|netilmicin)\b/,
  },
  {
    key: 'paracetamol', label: 'paracetamol',
    aliases: /\b(paracetamol|acetaminophen|crocin|dolo|calpol|pcm)\b/,
    members: /\b(paracetamol|acetaminophen)\b/,
  },
  {
    key: 'opioid', label: 'an opioid',
    aliases: /\b(opioids?|opiates?|codeine|morphine|tramadol)\b/,
    members: /\b(codeine|morphine|tramadol|tapentadol|fentanyl|pethidine|oxycodone|hydrocodone|dihydrocodeine)\b/,
  },
  {
    // Aromatic anticonvulsants cross-react (serious skin reactions)
    key: 'aromatic-anticonvulsant', label: 'an aromatic anticonvulsant',
    aliases: /\b(carbamazepine|tegretol|phenytoin|eptoin|oxcarbazepine|lamotrigine|phenobarbit\w*)\b/,
    members: /\b(carbamazepine|phenytoin|fosphenytoin|oxcarbazepine|lamotrigine|phenobarbit\w*)\b/,
  },
];

const FAMILY_BY_KEY = new Map(FAMILIES.map(f => [f.key, f]));
const NONE = /^(nil|none|no|na|n\/a|nkda|nka|not known|no known( drug)? allergi\w*|unknown|-|—)$/i;

/** Split free-text allergies into terms; drops "nil", "NKDA" and similar. */
export function parseAllergies(text: string | null | undefined): string[] {
  return (text ?? '')
    .split(/[,;/\n]|\band\b|&/i)
    .map(t => t.replace(/\(.*?\)/g, '').replace(/allerg\w*( to)?/i, '').trim())
    .filter(t => t.length >= 3 && !NONE.test(t));
}

// Ingredient names from composition text: "Amoxicillin 250mg/5ml + Clavulanic acid 62.5mg" → ["amoxicillin", "clavulanic acid"]
function ingredientsOf(text: string): string[] {
  return text
    .toLowerCase()
    .split(/\+|,|\bwith\b/)
    .map(p => p.replace(/\(.*?\)/g, ' ').replace(/[\d.]+\s*(mg|mcg|g|ml|iu|%|meq)\b.*$/i, '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

/**
 * Check one medicine against the patient's allergy terms.
 * `medicineText` should include the composition (generic name) and the product name.
 */
export function checkAllergies(allergyTerms: string[], medicineText: { composition?: string | null; product?: string | null }): AllergyHit[] {
  if (!allergyTerms.length) return [];
  const composition = medicineText.composition ?? '';
  const product = medicineText.product ?? '';
  const ingredients = ingredientsOf(composition);
  // Free-text rows (no catalog composition) are matched on the product name itself
  const haystacks = ingredients.length ? ingredients : [product.toLowerCase()];
  const hits: AllergyHit[] = [];
  const seen = new Set<string>();
  const push = (h: AllergyHit) => { const k = `${h.level}|${h.allergy}|${h.ingredient}`; if (!seen.has(k)) { seen.add(k); hits.push(h); } };

  for (const term of allergyTerms) {
    const t = term.toLowerCase();
    const families = FAMILIES.filter(f => f.aliases.test(t));
    for (const ing of haystacks) {
      // 1. The allergy names this exact ingredient ("amoxicillin" allergy, amoxicillin prescribed).
      //    Whole words only: a "sulfa" allergy must not match "zinc sulfate".
      if (new RegExp(`\\b${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(ing)) {
        push({ level: 'error', allergy: term, ingredient: ing, text: `Allergy: ${term} — contains ${ing}` });
        continue;
      }
      for (const f of families) {
        // 2. Same drug family ("penicillin" allergy, amoxicillin prescribed)
        if (f.members.test(ing)) {
          push({ level: 'error', allergy: term, ingredient: ing, text: `Allergy: ${term} — ${ing} is ${f.label}` });
          continue;
        }
        // 3. Known cross-reaction ("penicillin" allergy, cephalosporin prescribed)
        for (const c of f.crossReacts ?? []) {
          const other = FAMILY_BY_KEY.get(c.family)!;
          if (other.members.test(ing)) {
            push({ level: 'warning', allergy: term, ingredient: ing, text: `Allergy: ${term} — ${ing} is ${other.label}, ${c.note}` });
          }
        }
      }
    }
  }
  // Errors first
  return hits.sort((a, b) => (a.level === b.level ? 0 : a.level === 'error' ? -1 : 1));
}
